import React, { useEffect } from "react";
import { Button, downloadFile, Modal, Table, Typography, useAlerts, useLoading } from "best-common-react";
import { EmailListRow, EmailListUploadSummary } from "../types/emailList.ts";
import { downloadEmailListSummaryExport, getEmailListSummary, resendEmailList } from "../api/AuthenticatedClient.ts";
import styled from "styled-components";
import { formatCreatedAt } from "../util/DateUtil.ts";

const CanResendStatues = ["List Ready in Bullseye", "Opt-ins Processed", "Sent to ESP"];

const BodyContainer = styled.div`
  min-height: 200px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type FieldMapping = {
  header: string;
  field: string;
};

type EmailListUploadSummaryModalProps = {
  show: boolean;
  emailListRow?: EmailListRow;
  refresh: () => void;
  onClose: () => void;
};

const EmailListUploadSummaryModal: React.FC<EmailListUploadSummaryModalProps> = ({ show, emailListRow, refresh, onClose }) => {
  const { setLoading } = useLoading();
  const { addAlert } = useAlerts();
  const [summary, setSummary] = React.useState<EmailListUploadSummary>();
  const [canResend, setCanResend] = React.useState<boolean>(false);
  const [fieldMappings, setFieldMappings] = React.useState<FieldMapping[]>([]);

  useEffect(() => {
    if (!!emailListRow?.status) {
      setCanResend(CanResendStatues.includes(emailListRow.status));
    } else {
      setCanResend(false);
    }
  }, [emailListRow]);

  useEffect(() => {
    if (emailListRow) {
      setLoading(true);
      getEmailListSummary(emailListRow.id as number)
        .then(setSummary)
        .finally(() => setLoading(false));
    } else {
      setSummary(undefined);
    }
  }, [emailListRow]);

  useEffect(() => {
    const fields = emailListRow?.fields?.split(",") || [];
    const headers = emailListRow?.headers?.split(",") || [];

    const zipped: FieldMapping[] = fields.map((field, index) => ({
      header: headers[index] || "",
      field: field,
    }));

    setFieldMappings(zipped);
  }, [emailListRow?.fields, emailListRow?.headers]);

  useEffect(() => {
    if (!show) {
      setSummary(undefined);
    }
  }, [show]);

  const onResend = async () => {
    setLoading(true);
    await resendEmailList(emailListRow?.id as number);
    refresh();
  };

  const onExport = async () => {
    try {
      setLoading(true);
      const response = await downloadEmailListSummaryExport(emailListRow?.id as number);
      downloadFile(response);
    } catch (e) {
      // @ts-ignore
      if (e.status === 404) {
        addAlert({
          text: "Email List Upload Summary Export Not Found",
          type: "danger"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} size="lg">
      <Modal.Header>
        <HeaderContainer>
          <Typography variant="h5"> {emailListRow?.name}</Typography>
          <Typography variant="body1" className="m-0">
            Status: {emailListRow?.status}
          </Typography>
          <Typography variant="body1" className="m-0">
            Upload by {emailListRow?.createdBy} on {formatCreatedAt(emailListRow?.createdAt)}
          </Typography>
        </HeaderContainer>
      </Modal.Header>
      <Modal.Body styles={{ background: "white" }}>
        <BodyContainer>
          <Table>
            <Table.Head>
              <Table.Header>Summary Report</Table.Header>
              <Table.Header />
            </Table.Head>
            <Table.Body>
              {summary?.summaryReport.map(({ key, value }) => (
                <Table.Row key={key}>
                  <Table.Td>{key}</Table.Td>
                  <Table.Td>{value}</Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <div className="mt-4">
            <Table>
              <Table.Head>
                <Table.Header>Field Mappings</Table.Header>
                <Table.Header />
              </Table.Head>
              <Table.Body>
                {fieldMappings.map(({ field, header }) => (
                  <Table.Row key={`mapping-${field}`}>
                    <Table.Td>{header}</Table.Td>
                    <Table.Td>{field}</Table.Td>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>

        </BodyContainer>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" className="me-2" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" className="me-2" onClick={onResend} disabled={!canResend}>
          Resend
        </Button>
        <Button variant="primary" onClick={onExport} disabled={!summary?.hasExport}>
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailListUploadSummaryModal;
